/**
 * Parsing and formatting duration values.
 *
 * A duration is stored as a number of seconds and displayed through one of the
 * formats below. Both directions are format-sensitive and neither is reversible
 * on its own: `1d 12` means twelve hours under `d h` and twelve minutes under
 * `d h:mm`, and displaying at `h:mm` discards everything below a minute.
 *
 * Kept in step with `duration.py` on the backend, which parses the same strings
 * for imports and formulas. A difference between the two shows up as a value
 * that changes when it makes a round trip.
 */

/** The formats a duration field can be configured with, in display order. */
export const DURATION_FORMATS = [
  'h:mm',
  'h:mm:ss',
  'h:mm:ss.s',
  'h:mm:ss.ss',
  'h:mm:ss.sss',
  'd h',
  'd h:mm',
  'd h:mm:ss',
  'd h mm',
  'd h mm ss',
] as const

export type DurationFormat = (typeof DURATION_FORMATS)[number]

export const DEFAULT_DURATION_FORMAT: DurationFormat = 'h:mm'

/** `timedelta.max.total_seconds()` on the backend, which refuses anything past it. */
export const MAX_DURATION_SECONDS = 86_400_000_000_000
export const MIN_DURATION_SECONDS = -MAX_DURATION_SECONDS

const SECONDS_IN_DAY = 86_400
const SECONDS_IN_HOUR = 3_600
const SECONDS_IN_MINUTE = 60

/**
 * A length of time in milliseconds.
 *
 * A class rather than a number so a duration cannot be passed where a count of
 * seconds is wanted, which is the mistake the two representations invite: the
 * backend serialises seconds and this module works in milliseconds.
 */
export class Timedelta {
  readonly ms: number

  constructor(ms: number) {
    this.ms = ms
  }
}

export function isDurationFormat(value: unknown): value is DurationFormat {
  return (DURATION_FORMATS as readonly unknown[]).includes(value)
}

/**
 * The format a duration column is configured for, or the default.
 *
 * Every reader of a duration needs this and each one used to work it out for
 * itself -- the editor, the filters and now the validator -- which is three
 * chances to disagree about what an unconfigured column means.
 */
export function durationFormatOf(field: object): DurationFormat {
  const format = 'duration_format' in field ? field.duration_format : undefined
  return isDurationFormat(format) ? format : DEFAULT_DURATION_FORMAT
}

// ---------------------------------------------------------------------------
// The token format engine
// ---------------------------------------------------------------------------

type TokenField = 'days' | 'hours' | 'minutes' | 'seconds'
type TokenizedField = TokenField | 'fraction'

/** Longest first, so `hh` is not read as `h` followed by a literal `h`. */
const DURATION_FORMAT_TOKENS: readonly (readonly [string, TokenField])[] = [
  ['hh', 'hours'],
  ['mm', 'minutes'],
  ['ss', 'seconds'],
  ['d', 'days'],
  ['h', 'hours'],
  ['m', 'minutes'],
  ['s', 'seconds'],
]

export interface TokenizedFormat {
  pattern: RegExp
  fields: TokenizedField[]
}

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Turns a token format into a matcher and the order of the fields it captures.
 *
 * A backslash escapes the next character so it is matched and emitted as a
 * literal even when it is a token letter, which is what lets a format carry unit
 * suffixes -- `d\d h\h` renders "1d 2h". A run of `f` is the fractional-seconds
 * token and its length is the precision, so `ss.fff` keeps milliseconds.
 *
 * Returns null for a format that is unusable: an empty string, a repeated field,
 * a trailing backslash with nothing to escape, or one with no token at all.
 */
export function tokenizeDurationFormat(format: string): TokenizedFormat | null {
  if (typeof format !== 'string' || format.length === 0) return null

  const pattern: string[] = ['^-?']
  const fields: TokenizedField[] = []
  const seen = new Set<TokenizedField>()
  let i = 0

  while (i < format.length) {
    if (format[i] === '\\') {
      if (i + 1 >= format.length) return null
      pattern.push(escapeRegExp(format[i + 1] as string))
      i += 2
      continue
    }

    if (format[i] === 'f') {
      if (seen.has('fraction')) return null
      seen.add('fraction')
      let precision = 0
      while (i < format.length && format[i] === 'f') {
        precision += 1
        i += 1
      }
      fields.push('fraction')
      // At most `precision` digits; a longer run simply fails to match.
      pattern.push(`(\\d{1,${String(precision)}})`)
      continue
    }

    const token = DURATION_FORMAT_TOKENS.find(([text]) => format.startsWith(text, i))
    if (token === undefined) {
      pattern.push(escapeRegExp(format[i] as string))
      i += 1
      continue
    }

    const [text, field] = token
    if (seen.has(field)) return null
    seen.add(field)
    fields.push(field)
    pattern.push('(\\d+)')
    i += text.length
  }

  if (fields.length === 0) return null

  pattern.push('$')
  return { pattern: new RegExp(pattern.join('')), fields }
}

export const isValidDurationFormat = (value: string): boolean =>
  tokenizeDurationFormat(value) !== null

/**
 * The length of the `f` run in a format, or 0 when it has none.
 *
 * Walks the format honouring backslash escapes, so an escaped `\f` literal is
 * not counted as the fraction token.
 */
function fractionPrecision(format: string): number {
  let i = 0
  while (i < format.length) {
    if (format[i] === '\\') {
      i += 2
      continue
    }
    if (format[i] === 'f') {
      let precision = 0
      while (i < format.length && format[i] === 'f') {
        precision += 1
        i += 1
      }
      return precision
    }
    i += 1
  }
  return 0
}

/** Reads a duration written in exactly the given token format. */
export function parseValueWithDurationFormat(
  value: string,
  format: string,
): Timedelta | null {
  if (typeof value !== 'string') return null

  const tokenized = tokenizeDurationFormat(format)
  if (tokenized === null) return null

  const stripped = value.trim()
  const match = tokenized.pattern.exec(stripped)
  if (match === null) return null

  const parts: Record<TokenField, number> = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  }
  let fractionMs = 0

  tokenized.fields.forEach((field, index) => {
    const group = match[index + 1] as string
    if (field === 'fraction') {
      // Positional decimal digits: "5" is 0.5, "05" is 0.05, "234" is 0.234.
      fractionMs = (parseInt(group, 10) / 10 ** group.length) * 1000
    } else {
      parts[field] = parseInt(group, 10)
    }
  })

  const ms =
    parts.days * SECONDS_IN_DAY * 1000 +
    parts.hours * SECONDS_IN_HOUR * 1000 +
    parts.minutes * SECONDS_IN_MINUTE * 1000 +
    parts.seconds * 1000 +
    fractionMs

  return new Timedelta(stripped.startsWith('-') ? -ms : ms)
}

/** Writes a duration in the given token format. */
export function formatValueWithDurationFormat(
  value: Timedelta,
  format: string,
): string | null {
  if (!(value instanceof Timedelta)) return null

  const tokenized = tokenizeDurationFormat(format)
  if (tokenized === null) return null

  const present = new Set(tokenized.fields)
  const negative = value.ms < 0
  const absoluteMs = Math.abs(value.ms)
  const precision = present.has('fraction') ? fractionPrecision(format) : 0

  // Rounded to the target precision *before* being split into fields, so a value
  // that rounds up carries: 59.9996 at three digits becomes 60.000 and rolls into
  // a minute. Rounding each field separately would render "1:59.996" as "1:60.00".
  const factor = 10 ** precision
  const total = Math.round((absoluteMs / 1000) * factor) / factor
  const totalSeconds = Math.trunc(total)
  const fraction = total - totalSeconds

  const values: Record<TokenField, number> = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  }

  // Each unit holds the remainder of the largest unit present above it, so a
  // format that omits days shows their hours rather than dropping them.
  if (present.has('days')) {
    values.days = Math.trunc(totalSeconds / SECONDS_IN_DAY)
  }
  if (present.has('hours')) {
    values.hours = present.has('days')
      ? Math.trunc((totalSeconds % SECONDS_IN_DAY) / SECONDS_IN_HOUR)
      : Math.trunc(totalSeconds / SECONDS_IN_HOUR)
  }
  if (present.has('minutes')) {
    values.minutes =
      present.has('hours') || present.has('days')
        ? Math.trunc((totalSeconds % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE)
        : Math.trunc(totalSeconds / SECONDS_IN_MINUTE)
  }
  if (present.has('seconds')) {
    values.seconds =
      present.has('minutes') || present.has('hours') || present.has('days')
        ? totalSeconds % SECONDS_IN_MINUTE
        : totalSeconds
  }

  const out: string[] = []
  let i = 0
  while (i < format.length) {
    if (format[i] === '\\') {
      // The format tokenized, so there is always a character to escape.
      out.push(format[i + 1] as string)
      i += 2
      continue
    }
    if (format[i] === 'f') {
      out.push(String(Math.round(fraction * 10 ** precision)).padStart(precision, '0'))
      i += precision
      continue
    }
    const token = DURATION_FORMAT_TOKENS.find(([text]) => format.startsWith(text, i))
    if (token === undefined) {
      out.push(format[i] as string)
      i += 1
      continue
    }
    const [text, field] = token
    const rendered = String(values[field])
    out.push(text.length === 2 ? rendered.padStart(2, '0') : rendered)
    i += text.length
  }

  const result = out.join('')
  return negative ? `-${result}` : result
}

// ---------------------------------------------------------------------------
// Reading what somebody typed
// ---------------------------------------------------------------------------

type DurationPart = 'days' | 'hours' | 'minutes' | 'seconds'

interface DurationInputPattern {
  regex: RegExp
  /**
   * What each capturing group means, by position.
   *
   * Format-sensitive, because the same string means different things under
   * different formats: `1d 12` is twelve hours at `d h` and twelve minutes at
   * `d h:mm`, and a bare `123` is hours, minutes or seconds depending on the
   * smallest unit the format displays. `parts` is the fallback.
   */
  parts: DurationPart[]
  perFormat?: Partial<Record<DurationFormat, DurationPart[]>>
}

/** Named-group form, for the free-form `1d 2h 3m 4s` input. */
const UNIT_SUFFIXED =
  /^((?<days>\d+)(?:d\s*))?((?<hours>\d+)(?:h\s*))?((?<minutes>\d+)(?:m\s*))?((?<seconds>\d+|\d+\.\d+)(?:s\s*))?$/

/**
 * Tried in order, first match wins. Order is load-bearing: the more specific
 * patterns have to come before the ones that would also match them.
 */
const DURATION_INPUT_PATTERNS: readonly DurationInputPattern[] = [
  // 1d 12:13:14.123
  {
    regex: /^(\d+)(?:d\s*|\s+)(\d+):(\d+):(\d+|\d+\.\d+)$/,
    parts: ['days', 'hours', 'minutes', 'seconds'],
  },
  // 1:11:12.134
  { regex: /^(\d+):(\d+):(\d+|\d+\.\d+)$/, parts: ['hours', 'minutes', 'seconds'] },
  // 1d 12:13
  {
    regex: /^(\d+)(?:d\s*|\s+)(\d+):(\d+)$/,
    parts: ['days', 'minutes', 'seconds'],
    perFormat: {
      'd h': ['days', 'hours', 'minutes'],
      'd h:mm': ['days', 'hours', 'minutes'],
      'd h mm': ['days', 'hours', 'minutes'],
    },
  },
  // 1d 11:12.5
  {
    regex: /^(\d+)(?:d\s*|\s+)(\d+):(\d+\.\d+)$/,
    parts: ['days', 'minutes', 'seconds'],
  },
  // 123h
  { regex: /^(\d+)h$/, parts: ['hours'] },
  // 1d 12h
  { regex: /^(\d+)(?:d\s*|\s+)(\d+)h$/, parts: ['days', 'hours'] },
  // 123d
  { regex: /^(\d+)d$/, parts: ['days'] },
  // 1d 12
  {
    regex: /^(\d+)(?:d\s*|\s+)(\d+)$/,
    parts: ['days', 'seconds'],
    perFormat: {
      'd h': ['days', 'hours'],
      'd h:mm': ['days', 'minutes'],
      'h:mm': ['days', 'minutes'],
      'd h mm': ['days', 'minutes'],
    },
  },
  // 1d 123.234
  { regex: /^(\d+)(?:d\s*|\s+)(\d+\.\d+)$/, parts: ['days', 'seconds'] },
  // 11:12
  {
    regex: /^(\d+):(\d+)$/,
    parts: ['minutes', 'seconds'],
    perFormat: {
      'd h': ['hours', 'minutes'],
      'd h:mm': ['hours', 'minutes'],
      'h:mm': ['hours', 'minutes'],
      'd h mm': ['hours', 'minutes'],
    },
  },
  // 123:12.123
  { regex: /^(\d+):(\d+\.\d+)$/, parts: ['minutes', 'seconds'] },
  // 123.2134
  { regex: /^(\d+\.\d+)$/, parts: ['seconds'] },
  // 123
  {
    regex: /^(\d+)$/,
    parts: ['seconds'],
    perFormat: {
      'd h': ['hours'],
      'd h:mm': ['minutes'],
      'h:mm': ['minutes'],
      'd h mm': ['minutes'],
    },
  },
]

const PART_IN_SECONDS: Record<DurationPart, number> = {
  days: SECONDS_IN_DAY,
  hours: SECONDS_IN_HOUR,
  minutes: SECONDS_IN_MINUTE,
  seconds: 1,
}

function toSeconds(values: Partial<Record<DurationPart, string>>): number {
  let total = 0
  for (const [part, raw] of Object.entries(values) as [DurationPart, string][]) {
    // Only seconds can be fractional; the rest are whole by construction of the
    // patterns, and reading them as floats would accept `1.5d`.
    const parsed = part === 'seconds' ? parseFloat(raw) : parseInt(raw, 10)
    total += parsed * PART_IN_SECONDS[part]
  }
  return total
}

/**
 * Reads a duration a person typed, in seconds, or null when it is not one.
 *
 * The format is needed because the same input is ambiguous without it. A number
 * already in seconds -- which is how the backend sends them -- is returned
 * unchanged rather than re-read as text.
 */
export function parseDurationValue(
  input: string | number | null | undefined,
  format: DurationFormat | 'h:mm:ss.sss' = 'h:mm:ss.sss',
): number | null {
  if (input === null || input === undefined || input === '') return null
  if (Number.isFinite(input)) return input as number
  if (typeof input !== 'string') return null

  const negative = input.startsWith('-')
  const value = negative ? input.slice(1) : input
  const sign = negative ? -1 : 1

  const suffixed = UNIT_SUFFIXED.exec(value)
  if (suffixed?.groups !== undefined) {
    const groups = suffixed.groups
    const present = Object.fromEntries(
      Object.entries(groups).filter(([, raw]) => raw !== undefined),
    ) as Partial<Record<DurationPart, string>>
    // The pattern is entirely optional groups, so it also matches the empty
    // string. An input that matched but captured nothing is not a duration.
    if (Object.keys(present).length > 0) {
      return toSeconds(present) * sign
    }
  }

  for (const { regex, parts, perFormat } of DURATION_INPUT_PATTERNS) {
    const match = regex.exec(value)
    if (match === null) continue

    const names = perFormat?.[format] ?? parts
    const values: Partial<Record<DurationPart, string>> = {}
    names.forEach((part, index) => {
      values[part] = match[index + 1] as string
    })
    return toSeconds(values) * sign
  }

  return null
}

/** Plain-English durations, for pasting values like "3 weeks". */
const SPELLED_OUT_DURATIONS: readonly { regex: RegExp; days: number }[] = [
  { regex: /^(\d+)\s+years?$/i, days: 365 },
  { regex: /^(\d+)\s+months?$/i, days: 30 },
  { regex: /^(\d+)\s+weeks?$/i, days: 7 },
  { regex: /^(\d+)\s+days?$/i, days: 1 },
]

const SPELLED_OUT_UNITS: readonly { regex: RegExp; seconds: number }[] = [
  { regex: /^(\d+)\s+hours?$/i, seconds: SECONDS_IN_HOUR },
  { regex: /^(\d+)\s+minutes?$/i, seconds: SECONDS_IN_MINUTE },
  { regex: /^(\d+)\s+seconds?$/i, seconds: 1 },
]

/**
 * Reads a duration written any way this application accepts.
 *
 * Wider than `parseDurationValue`: it also takes the spelled-out forms, which
 * turn up when a value is pasted in from a spreadsheet rather than typed into a
 * cell.
 */
export function parseDurationString(
  value: string | number | null | undefined,
): Timedelta | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value !== 'string' && !Number.isFinite(value)) return null

  const seconds = parseDurationValue(value, 'h:mm:ss.sss')
  if (seconds !== null) return new Timedelta(seconds * 1000)

  if (typeof value !== 'string') return null
  const trimmed = value.trim()

  for (const { regex, days } of SPELLED_OUT_DURATIONS) {
    const match = regex.exec(trimmed)
    if (match !== null) {
      return new Timedelta(
        parseInt(match[1] as string, 10) * days * SECONDS_IN_DAY * 1000,
      )
    }
  }
  for (const { regex, seconds: unit } of SPELLED_OUT_UNITS) {
    const match = regex.exec(trimmed)
    if (match !== null) {
      return new Timedelta(parseInt(match[1] as string, 10) * unit * 1000)
    }
  }

  return null
}

// ---------------------------------------------------------------------------
// Displaying and rounding
// ---------------------------------------------------------------------------

interface DurationFormatInfo {
  /** A sample of the format, for the field configuration form. */
  description: string
  example: string
  /** Drops the precision the format cannot display. */
  round: (seconds: number) => number
  /**
   * The equivalent token format. The `d`-prefixed display formats need
   * backslash-escaped unit letters, which the token engine cannot emit
   * otherwise. Kept in step with `duration.py::DURATION_TOKEN_FORMATS`.
   */
  tokenFormat: string
}

const roundTo =
  (unit: number) =>
  (seconds: number): number =>
    Math.round(seconds / unit) * unit

const roundToDigits =
  (digits: number) =>
  (seconds: number): number => {
    const factor = 10 ** digits
    return Math.round(seconds * factor) / factor
  }

export const DURATION_FORMAT_INFO: Record<DurationFormat, DurationFormatInfo> = {
  'h:mm': {
    description: 'h:mm (1:23)',
    example: '1:23',
    round: roundTo(SECONDS_IN_MINUTE),
    tokenFormat: 'h:mm',
  },
  'h:mm:ss': {
    description: 'h:mm:ss (1:23:40)',
    example: '1:23:40',
    round: roundToDigits(0),
    tokenFormat: 'h:mm:ss',
  },
  'h:mm:ss.s': {
    description: 'h:mm:ss.s (1:23:40.0)',
    example: '1:23:40.0',
    round: roundToDigits(1),
    tokenFormat: 'h:mm:ss.f',
  },
  'h:mm:ss.ss': {
    description: 'h:mm:ss.ss (1:23:40.00)',
    example: '1:23:40.00',
    round: roundToDigits(2),
    tokenFormat: 'h:mm:ss.ff',
  },
  'h:mm:ss.sss': {
    description: 'h:mm:ss.sss (1:23:40.000)',
    example: '1:23:40.000',
    round: roundToDigits(3),
    tokenFormat: 'h:mm:ss.fff',
  },
  'd h': {
    description: 'd h (1d 2h)',
    example: '1d 2h',
    round: roundTo(SECONDS_IN_HOUR),
    tokenFormat: 'd\\d h\\h',
  },
  'd h:mm': {
    description: 'd h:mm (1d 2:34)',
    example: '1d 2:34',
    round: roundTo(SECONDS_IN_MINUTE),
    tokenFormat: 'd\\d h:mm',
  },
  'd h:mm:ss': {
    description: 'd h:mm:ss (1d 2:34:56)',
    example: '1d 2:34:56',
    round: roundToDigits(0),
    tokenFormat: 'd\\d h:mm:ss',
  },
  'd h mm': {
    description: 'd h m (1d 2h 3m)',
    example: '1d 2h 3m',
    round: roundTo(SECONDS_IN_MINUTE),
    tokenFormat: 'd\\d h\\h mm\\m',
  },
  'd h mm ss': {
    description: 'd h m s (1d 2h 3m 4s)',
    example: '1d 2h 3m 4s',
    round: roundToDigits(0),
    tokenFormat: 'd\\d h\\h mm\\m ss\\s',
  },
}

/** Drops the precision a format cannot display, so what is stored is what is shown. */
export function roundDurationValueToFormat(
  seconds: number | null,
  format: DurationFormat,
): number | null {
  if (seconds === null) return null

  const info = DURATION_FORMAT_INFO[format] as DurationFormatInfo | undefined
  if (info === undefined) {
    throw new Error(`Unknown duration format ${format}`)
  }
  return info.round(seconds)
}

/** Renders a number of seconds in the given format. */
export function formatDurationValue(
  seconds: number | null | undefined | '',
  format: DurationFormat,
): string {
  if (seconds === null || seconds === undefined || seconds === '') return ''

  return (
    formatValueWithDurationFormat(
      new Timedelta(seconds * 1000),
      DURATION_FORMAT_INFO[format].tokenFormat,
    ) ?? ''
  )
}
